import { z } from 'zod';

const timeOfDay = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Time must be HH:MM (24-hour, zero-padded)');

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD');

export const businessHoursWindowSchema = z
  .object({
    open: timeOfDay,
    close: timeOfDay,
  })
  .refine((w) => w.close > w.open, {
    message: 'close must be after open',
    path: ['close'],
  });

export const businessHoursHolidaySchema = z.object({
  date: isoDate,
  label: z.string().min(1),
  closed: z.boolean().default(true),
  hours: z.array(businessHoursWindowSchema).optional(),
});

export const businessHoursDayKeySchema = z.enum(['1', '2', '3', '4', '5', '6', '7']);

export const businessHoursSchema = z.object({
  schedule: z.partialRecord(businessHoursDayKeySchema, z.array(businessHoursWindowSchema)),
  holidays: z.array(businessHoursHolidaySchema).default([]),
});

const slugRegex = /^[a-z0-9-]+$/;

export const serviceBookingRulesSchema = z.object({
  minLeadTimeMinutes: z.number().int().nonnegative().default(0),
  maxAdvanceDays: z.number().int().positive().default(90),
  allowMultiDay: z.boolean().default(false),
  requiresVaccinationCheck: z.boolean().default(true),
});

export const serviceEntrySchema = z.object({
  slug: z.string().regex(slugRegex, 'Slug must be lowercase letters, digits, and hyphens only'),
  displayName: z.string().min(1),
  priceCents: z.number().int().nonnegative(),
  durationMinutes: z.number().int().positive(),
  applicableStaffRoles: z.array(z.string().min(1)).default([]),
  bookingRules: serviceBookingRulesSchema,
  active: z.boolean().default(true),
});

export const serviceCatalogSchema = z.array(serviceEntrySchema);

const einRegex = /^\d{2}-\d{7}$/;

export const businessAddressSchema = z.object({
  line1: z.string().min(1),
  line2: z.string().optional(),
  city: z.string().min(1),
  state: z.string().length(2),
  postalCode: z.string().min(1),
  country: z.string().length(2).default('US'),
});

export const businessMetaSchema = z.object({
  legalEntityName: z.string().min(1),
  entityType: z.enum(['LLC', 'Corp', 'S-Corp', 'SoleProp', 'Partnership', 'Other']),
  einTaxId: z.string().regex(einRegex, 'EIN must be formatted NN-NNNNNNN').optional(),
  primaryAddress: businessAddressSchema,
  primaryPhone: z.string().min(1),
  retentionOverrideDays: z.number().int().positive().optional(),
});

export const defaultRoleSchema = z.object({
  slug: z.string().regex(slugRegex, 'Role slug must be lowercase letters, digits, and hyphens only'),
  displayName: z.string().min(1),
  seededAtProvisioning: z.boolean().default(true),
  assignedAtProvisioning: z.boolean().default(false),
});

export const defaultRolesSchema = z.array(defaultRoleSchema);

const isValidTimeZone = (tz: string): boolean => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
};

const isValidLocale = (locale: string): boolean => {
  try {
    new Intl.Locale(locale);
    return true;
  } catch {
    return false;
  }
};

export const ianaTimezoneSchema = z
  .string()
  .refine(isValidTimeZone, 'Timezone must be a valid IANA zone (e.g. America/Denver)');

export const iso4217CurrencySchema = z
  .string()
  .regex(/^[A-Z]{3}$/, 'Currency must be an ISO 4217 three-letter uppercase code');

export const bcp47LocaleSchema = z
  .string()
  .refine(isValidLocale, 'Locale must be a valid BCP 47 tag (e.g. en-US)');

export const tenantConfigSchema = z.object({
  timezone: ianaTimezoneSchema.default('America/Denver'),
  currency: iso4217CurrencySchema.default('USD'),
  locale: bcp47LocaleSchema.default('en-US'),
  businessHours: businessHoursSchema,
  serviceCatalog: serviceCatalogSchema,
  businessMeta: businessMetaSchema,
  terminology: z.record(z.string(), z.string()).optional(),
  defaultRoles: defaultRolesSchema.default([]),
});

export type BusinessHoursWindow = z.infer<typeof businessHoursWindowSchema>;
export type BusinessHoursHoliday = z.infer<typeof businessHoursHolidaySchema>;
export type BusinessHoursDayKey = z.infer<typeof businessHoursDayKeySchema>;
export type BusinessHours = z.infer<typeof businessHoursSchema>;
export type ServiceBookingRules = z.infer<typeof serviceBookingRulesSchema>;
export type ServiceEntry = z.infer<typeof serviceEntrySchema>;
export type ServiceCatalog = z.infer<typeof serviceCatalogSchema>;
export type BusinessAddress = z.infer<typeof businessAddressSchema>;
export type BusinessMeta = z.infer<typeof businessMetaSchema>;
export type DefaultRole = z.infer<typeof defaultRoleSchema>;
export type DefaultRoles = z.infer<typeof defaultRolesSchema>;
export type TenantConfig = z.infer<typeof tenantConfigSchema>;
